import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
  IFilterParams,
} from 'ag-grid-community';

import { format, parse } from 'date-fns';

// * The wire model must stay byte-compatible with ag-grid's provided
// * agDateColumnFilter (inRange): SSRM posts the request's filterModel to the
// * backend verbatim, and saved view presets store the same shape.
/** ag-grid date filter model: a range, or one of the Blank / Not blank presets (WAP-1643). */
type DateRangeFilterModel =
  | {
      filterType: 'date';
      type: 'inRange';
      dateFrom: string;
      dateTo: string;
    }
  | { filterType: 'date'; type: 'blank' | 'notBlank' };

type BlankPreset = 'blank' | 'notBlank';

const MODEL_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const DISPLAY_DATE_FORMAT = 'dd MMM yyyy, HH:mm';

// * Client-side grids evaluate rows locally, so the cell value must be read as
// * a Date. Grids bind dates as Date objects, unix timestamps, the app's
// * 'dd-MM-yyyy HH:mm' display strings, or MySQL/ISO strings - try them all.
function coerceCellDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number') {
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  if (typeof value === 'string' && value) {
    const appFormat = parse(value, 'dd-MM-yyyy HH:mm', new Date());
    if (!isNaN(appFormat.getTime())) {
      return appFormat;
    }
    const native = new Date(value.replace(' ', 'T'));
    return isNaN(native.getTime()) ? null : native;
  }
  return null;
}

// * Custom column filter replacing agDateColumnFilter: wraps the shared
// * app-date-range-selection panel (presets + range calendar + time pickers)
// * so date filtering matches the Feeds/Dashboards period picker (WAP-1589).
@Component({
  selector: 'app-ag-date-range-filter',
  templateUrl: './ag-date-range-filter.component.html',
  styleUrls: ['./ag-date-range-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgDateRangeFilterComponent implements IFilterAngularComp {
  private params!: IFilterParams;
  private hidePopup: (() => void) | null = null;

  /** Pending selection shown in the panel; committed on Apply. */
  fromDate: Date | null = null;
  toDate: Date | null = null;

  private appliedFrom: Date | null = null;
  private appliedTo: Date | null = null;
  /** Active Blank / Not blank preset; excludes a range. */
  private appliedPreset: BlankPreset | null = null;
  private readonly translate = inject(TranslateService);

  agInit(params: IFilterParams): void {
    this.params = params;
  }

  isFilterActive(): boolean {
    return (
      this.appliedPreset != null ||
      (this.appliedFrom != null && this.appliedTo != null)
    );
  }

  // * Only client-side grids call this; SSRM sends the model to the backend.
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const { appliedFrom, appliedTo, appliedPreset } = this;
    const cellDate = coerceCellDate(this.params.getValue(params.node));
    if (appliedPreset) {
      return appliedPreset === 'blank' ? cellDate == null : cellDate != null;
    }
    if (!appliedFrom || !appliedTo) return true;
    if (!cellDate) return true;

    const time = cellDate.getTime();
    return time >= appliedFrom.getTime() && time <= appliedTo.getTime();
  }

  getModel(): DateRangeFilterModel | null {
    const { appliedFrom, appliedTo, appliedPreset } = this;
    if (appliedPreset) return { filterType: 'date', type: appliedPreset };
    if (!appliedFrom || !appliedTo) return null;

    return {
      filterType: 'date',
      type: 'inRange',
      dateFrom: format(appliedFrom, MODEL_DATE_FORMAT),
      dateTo: format(appliedTo, MODEL_DATE_FORMAT),
    };
  }

  setModel(model: DateRangeFilterModel | null): void {
    const range = model?.type === 'inRange' ? model : null;
    this.appliedPreset =
      model?.type === 'blank' || model?.type === 'notBlank' ? model.type : null;
    this.appliedFrom = range?.dateFrom
      ? parse(range.dateFrom, MODEL_DATE_FORMAT, new Date())
      : null;
    this.appliedTo = range?.dateTo
      ? parse(range.dateTo, MODEL_DATE_FORMAT, new Date())
      : null;
    this.fromDate = this.appliedFrom;
    this.toDate = this.appliedTo;
  }

  /** Read-only text shown in the floating filter row. */
  getModelAsString(): string {
    const { appliedFrom, appliedTo, appliedPreset } = this;
    if (appliedPreset) return this.presetLabel(appliedPreset);
    if (!appliedFrom || !appliedTo) return '';

    return `${format(appliedFrom, DISPLAY_DATE_FORMAT)} - ${format(appliedTo, DISPLAY_DATE_FORMAT)}`;
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams): void {
    this.hidePopup = params?.hidePopup ?? null;
    // Reopening discards any uncommitted selection from the previous visit.
    this.fromDate = this.appliedFrom;
    this.toDate = this.appliedTo;
  }

  onDateRangeSelection(range: { from: Date; to: Date }): void {
    this.fromDate = range.from;
    this.toDate = range.to;
  }

  apply(): void {
    if (!this.fromDate || !this.toDate) return;
    this.appliedPreset = null;
    this.appliedFrom = this.fromDate;
    this.appliedTo = this.toDate;
    this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  /** Blank / Not blank apply immediately and replace any range. */
  applyPreset(preset: BlankPreset): void {
    this.appliedPreset = preset;
    this.appliedFrom = null;
    this.appliedTo = null;
    this.fromDate = null;
    this.toDate = null;
    this.params.filterChangedCallback();
    this.hidePopup?.();
  }

  isPresetActive(preset: BlankPreset): boolean {
    return this.appliedPreset === preset;
  }

  private presetLabel(preset: BlankPreset): string {
    return this.translate.instant(
      preset === 'blank'
        ? 'ag-date-range-filter.button.blank'
        : 'ag-date-range-filter.button.not-blank',
    );
  }

  /** Cancel / Escape: drop the uncommitted edits and close; the applied filter stays. */
  cancel(): void {
    this.fromDate = this.appliedFrom;
    this.toDate = this.appliedTo;
    this.hidePopup?.();
  }

  reset(): void {
    const wasActive = this.isFilterActive();

    this.fromDate = null;
    this.toDate = null;
    this.appliedFrom = null;
    this.appliedTo = null;
    this.appliedPreset = null;
    if (wasActive) {
      this.params.filterChangedCallback();
    }
    this.hidePopup?.();
  }
}
